import React, { useCallback } from 'react';
import { SearchField } from '@noths/polaris-client-ribbons-design-system';

import * as styles from './SearchBarContainer.styles';
import { SearchSuggestions } from 'client-site-chrome-ui/components/molecules/SearchSuggestions/SearchSuggestions';
import { useSearchContext } from 'client-site-chrome-ui/context/SearchContext/useSearchContext';
import { SEARCH_RESULTS_PATH } from 'client-site-chrome-ui/hooks/useSearch/utils/buildSearchPath';

export const SearchBarContainer = () => {
  const {
    search: {
      handleSearch,
      hideSuggestions,
      isSearchInFocus,
      isSuggestionsShown,
      resetAll,
      resetSelectedSuggestion,
      searchTerm,
      selectNextSearchSuggestion,
      selectPreviousSearchSuggestion,
      selectedSuggestionTerm,
      setSearchFocus,
      setSearchTerm,
    },
  } = useSearchContext();

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          selectPreviousSearchSuggestion();
          break;
        case 'ArrowDown':
          e.preventDefault();
          selectNextSearchSuggestion();
          break;
        case 'Escape':
          resetSelectedSuggestion();
          hideSuggestions();
          break;
        default:
          if (e.key !== 'Enter') {
            resetSelectedSuggestion();
          }
      }
    },
    [
      hideSuggestions,
      resetSelectedSuggestion,
      selectNextSearchSuggestion,
      selectPreviousSearchSuggestion,
    ],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSearch();
    },
    [handleSearch],
  );

  const isAnyChildStillInFocus = (e: React.FocusEvent) =>
    e.currentTarget.contains(e.relatedTarget as Node);

  const onBlur = useCallback(
    (e: React.FocusEvent) => {
      if (!isAnyChildStillInFocus(e)) {
        setSearchFocus(false);
      }
    },
    [setSearchFocus],
  );

  const onChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setSearchTerm!(e.currentTarget.value);
    },
    [setSearchTerm],
  );

  const onFocus = useCallback(() => {
    setSearchFocus(true);
  }, [setSearchFocus]);

  return (
    <div css={styles.container} onBlur={onBlur}>
      <form action={SEARCH_RESULTS_PATH} onSubmit={onSubmit} role="search">
        <SearchField
          ariaAutocomplete="list"
          ariaExpanded={isSuggestionsShown}
          ariaOwns="suggestionList"
          displayValue={selectedSuggestionTerm}
          id="search-term"
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onReset={resetAll}
          placeholder="Search our marketplace"
          showSubmitButtonAsFocused={isSearchInFocus}
          value={searchTerm}
        />
      </form>
      {isSuggestionsShown && <SearchSuggestions />}
    </div>
  );
};
